import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RotatingLines } from 'react-loader-spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faCalculator, faChartLine, faUsers } from '@fortawesome/free-solid-svg-icons';
import { apiBaseUrl } from '../config.js';
import MercadoPagoPayment from './MercadoPagoPayment.js';
import { logPageVisit, logUserAction, logSMPSearch, logPaymentInitiated, logAnalysisCompleted, logError, ACTIONS } from '../utils/logger';

function DevelopmentAnalysis({ onClose }) {
  // State for development analysis
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [manualSMP, setManualSMP] = useState('');
  const [showNoDataModal, setShowNoDataModal] = useState(false);
  
  // Payment state
  const [pendingAnalysis, setPendingAnalysis] = useState(null); // Stores analysis data before payment
  const [showPayment, setShowPayment] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Function to scroll to investment analysis section (when results are shown)
  const scrollToInvestmentAnalysis = () => {
    setTimeout(() => {
      // Primary: Look for the specific investment analysis section by ID
      const investmentSection = document.getElementById('investment-analysis-section');
      
      if (investmentSection) {
        investmentSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      } else {
        // Fallback: Look for the text content "Análisis de Inversión"
        const textBasedSection = Array.from(document.querySelectorAll('*')).find(el => 
          el.textContent && el.textContent.includes('Análisis de Inversión')
        );
        
        if (textBasedSection) {
          // Scroll to the parent container that contains the whole analysis
          const parentContainer = textBasedSection.closest('.row') || 
                                 textBasedSection.closest('.container') || 
                                 textBasedSection.closest('div[style*="background"]') ||
                                 textBasedSection;
          
          parentContainer.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        } else {
          // Final fallback: scroll to where results typically appear
          const developmentSection = document.getElementById('development-analysis-section');
          if (developmentSection) {
            developmentSection.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'end' 
            });
          }
        }
      }
    }, 1500); // Delay to ensure results are fully rendered
  };

  // Function to scroll to payment section (when payment is required)
  const scrollToPaymentSection = () => {
    setTimeout(() => {
      const paymentSection = document.getElementById('payment-section');
      
      if (paymentSection) {
        paymentSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      } else {
        // Fallback: scroll down from current position to show more content
        window.scrollBy({ 
          top: 400, 
          behavior: 'smooth' 
        });
      }
    }, 1500); // Delay to let the payment section render
  };

  // New function to verify payment and execute analysis
  const verifyPaymentAndExecuteAnalysis = async (paymentId) => {
    try {
      setError('');
      setSuccessMessage('Verificando pago...');
      setLoadingAnalysis(true); // Enable loading state for button
      
      // Check payment status in database
      const paymentResponse = await axios.get(`${apiBaseUrl}/api/check_payment/${paymentId}`);
      
      if (paymentResponse.data.success && paymentResponse.data.approved) {
        
        const smp = paymentResponse.data.smp;
        
        // Set the SMP and mark payment as completed
        setManualSMP(smp);
        setPaymentCompleted(true);
        setError('');
        setSuccessMessage('✅ Pago verificado! Ejecutando análisis...');
        
        // SMP analysis completed - user can use "Analizar otro SMP" for new searches
        
        // Auto-scroll to development section
        setTimeout(() => {
          const developmentSection = document.getElementById('development-analysis-section');
          if (developmentSection) {
            developmentSection.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 500);
        
        // Execute analysis with the SMP from database and payment_id
        setTimeout(async () => {
          try {
            const analysisResponse = await axios.post(`${apiBaseUrl}/api/analyze_building_potential`, {
              smp: smp,
              payment_id: paymentId
            }, {
              timeout: 60000
            });

            if (analysisResponse.data && analysisResponse.data.success) {
              const analysisData = analysisResponse.data.data;
              setAnalysisResult(analysisData);
              setShowPayment(false);
              setError('');
              setSuccessMessage('✅ Análisis completado exitosamente!');
              
              // Log successful analysis completion
              logAnalysisCompleted(smp);
              
              // Auto-scroll to investment analysis section
              scrollToInvestmentAnalysis();
              
              // Disable loading state after successful analysis
              setLoadingAnalysis(false);
            } else {
              setError('Error al ejecutar el análisis después del pago');
              setLoadingAnalysis(false);
            }
          } catch (analysisError) {
            setError('Error al ejecutar el análisis después del pago');
            setLoadingAnalysis(false);
          }
        }, 1000);
        
      } else {
        setSuccessMessage('');
        setError('El pago no fue encontrado o no está aprobado');
        setLoadingAnalysis(false);
      }
      
    } catch (error) {
      setSuccessMessage('');
      setError('Error al verificar el pago');
      setLoadingAnalysis(false);
    }
  };

  // Function to handle post-payment analysis (moved outside useEffect for reusability)
  const handlePostPayment = async () => {
      // Check if we should show development results after payment
      const showResults = localStorage.getItem('showDevelopmentResults');
      const paymentDone = localStorage.getItem('paymentCompleted');
      
      if (showResults === 'true' && paymentDone === 'true') {
        
        // Get payment_id from URL or localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const paymentId = urlParams.get('payment_id') || localStorage.getItem('mercadopago_payment_id');
        
        
        if (paymentId) {
          // Use the new payment verification flow
          await verifyPaymentAndExecuteAnalysis(paymentId);
          
          // Clean up flags
          localStorage.removeItem('showDevelopmentResults');
          localStorage.removeItem('paymentCompleted');
          localStorage.removeItem('mercadopago_payment_id');
          
          return; // Exit early since we're using the new flow
        }
        
        // Legacy fallback code (kept for compatibility)
        const lastSmp = localStorage.getItem('lastAnalyzedSmp');
        
        if (paymentId && lastSmp) {
          // Check if payment was successful from saved params or URL params
          let paymentStatus, collectionStatus;
          
          // Try to get from saved localStorage params first
          const savedParamsStr = localStorage.getItem('mercadopago_url_params');
          if (savedParamsStr) {
            try {
              const savedParams = JSON.parse(savedParamsStr);
              paymentStatus = savedParams.status;
              collectionStatus = savedParams.collection_status;
            } catch (error) {
            }
          }
          
          // Fallback to URL params if no saved params
          if (!paymentStatus && !collectionStatus) {
            const urlParams = new URLSearchParams(window.location.search);
            paymentStatus = urlParams.get('status');
            collectionStatus = urlParams.get('collection_status');
          }
          
          if (paymentStatus === 'approved' || collectionStatus === 'approved') {
            
            // Try to update payment status in database (fallback for missing webhook)
            try {
              await axios.post(`${apiBaseUrl}/api/validate_payment`, {
                paymentId: paymentId,
                smp: lastSmp
              });
            } catch (error) {
            }
          
            // Mark as completed and run analysis directly
            setPaymentCompleted(true);
            setManualSMP(lastSmp);
            setError('Pago confirmado! Ejecutando análisis...');
            
            // Auto-scroll to development section
            setTimeout(() => {
              const developmentSection = document.getElementById('development-analysis-section');
              if (developmentSection) {
                developmentSection.scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'start' 
                });
              }
            }, 500);
            
            setTimeout(() => {
              handleAnalyzeDevelopment();
            }, 1000);
          } else {
            // Validate payment normally
            validatePaymentAndAnalyze(paymentId, lastSmp);
          }
        } else if (!pendingAnalysis && lastSmp) {
          // No payment validation needed, just re-run analysis
          
          // Check if this is a localStorage fallback payment
          const pendingPaymentStr = localStorage.getItem('pendingMercadoPagoPayment');
          if (pendingPaymentStr) {
            try {
              const pendingPayment = JSON.parse(pendingPaymentStr);
              if (pendingPayment.smp === lastSmp && !pendingPayment.pendingPayment) {
                setManualSMP(pendingPayment.smp);
              } else {
                setManualSMP(lastSmp);
              }
            } catch (error) {
              setManualSMP(lastSmp);
            }
          } else {
            setManualSMP(lastSmp);
          }
          
          // Auto-scroll to development section
          setTimeout(() => {
            const developmentSection = document.getElementById('development-analysis-section');
            if (developmentSection) {
              developmentSection.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start' 
              });
            }
          }, 300);
          
          setTimeout(() => {
            handleAnalyzeDevelopment();
          }, 500);
        }
        
        // Clean up flags
        localStorage.removeItem('showDevelopmentResults');
        localStorage.removeItem('paymentCompleted');
        localStorage.removeItem('mercadopago_payment_id');
      }
    };

  // Effect to handle post-payment analysis trigger
  useEffect(() => {
    // Call the async function
    handlePostPayment();
  }, [pendingAnalysis]);
  
  // Log page visit when component loads
  useEffect(() => {
    logPageVisit('development_analysis');
    
    // Simple hit tracker for development analysis section
    axios.post(`${apiBaseUrl}/api/page_hit`, { section: 'development_analysis' })
  }, []);

  // Additional useEffect to check for payment_id in URL when component mounts
  useEffect(() => {
    const checkForPaymentId = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentId = urlParams.get('payment_id');
      
      
      if (paymentId) {
        
        // Auto-scroll to development section immediately
        setTimeout(() => {
          const developmentSection = document.getElementById('development-analysis-section');
          if (developmentSection) {
            developmentSection.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 500);
        
        // Execute payment verification and analysis
        verifyPaymentAndExecuteAnalysis(paymentId);
      } else {
        
        // Fallback: Check for legacy localStorage flags
        const showResults = localStorage.getItem('showDevelopmentResults');
        const paymentDone = localStorage.getItem('paymentCompleted');
        
        if (showResults === 'true' && paymentDone === 'true') {
          handlePostPayment();
        }
      }
    };
    
    // Check immediately and then after a short delay
    checkForPaymentId();
    const timeoutId = setTimeout(checkForPaymentId, 1000);
    
    return () => clearTimeout(timeoutId);
  }, []); // Run once when component mounts

  const handleAnalyzeDevelopment = async () => {
    // Note: For new SMP analysis, users should use "Analizar otro SMP" button
    // to start fresh from the main page
    
    if (!manualSMP || manualSMP.trim() === '') {
      
      // Try to get SMP from localStorage as fallback
      const fallbackSMP = localStorage.getItem('lastAnalyzedSmp');
      if (fallbackSMP && fallbackSMP.trim() !== '') {
        setManualSMP(fallbackSMP);
        // Don't return, let the function continue with the fallback SMP
      } else {
        setError('Por favor ingresa un código SMP');
        return;
      }
    }

    setLoadingAnalysis(true);
    setError('');
    setSuccessMessage('');

    // Use current SMP or fallback SMP  
    const finalSMP = manualSMP && manualSMP.trim() !== '' ? manualSMP : localStorage.getItem('lastAnalyzedSmp');
    
    // Save the SMP for potential post-payment use
    const smpCode = finalSMP.trim().toUpperCase();
    localStorage.setItem('lastAnalyzedSmp', smpCode);
    
    // Log SMP search action
    logSMPSearch(smpCode);

    try {
      // Use preview endpoint for initial analysis (no payment required)
      const response = await axios.post(`${apiBaseUrl}/api/preview_building_potential`, {
        smp: smpCode
      }, {
        timeout: 60000
      });

      if (response.data && response.data.success) {
        
        // Check if this is a preview response (requires payment)
        if (response.data.preview && response.data.requiresPayment) {
          
          // Create mock analysis data to show payment requirement
          const mockAnalysisData = {
            analysis: {
              maxBuildableArea: 1000, // Mock data to trigger payment flow
              maxFloors: 10,
              barrio: 'Preview',
              zoning: 'Preview'
            },
            address: `SMP: ${smpCode}`,
            message: response.data.message
          };
          
          // Show payment requirement
          setPendingAnalysis(mockAnalysisData);
          setShowPayment(true);
          setAnalysisResult(null);
          setError('');
          
          // Log payment requirement
          logPaymentInitiated(smpCode, 2500);
          
          // Auto-scroll to payment section
          scrollToPaymentSection();
          
        } else if (response.data.preview && !response.data.requiresPayment) {
          // SMP doesn't exist or has no data - show appropriate message
          setShowNoDataModal(true);
          setAnalysisResult(null);
          setPendingAnalysis(null);
          setShowPayment(false);
          setError('');
          
        } else {
          // This is a full response (shouldn't happen with preview endpoint)
          const analysisData = response.data.data;
          const backendPaymentCompleted = response.data.paymentCompleted || false;
          
          // Check if we have valid building data
          if (!analysisData || !analysisData.analysis || 
              !analysisData.analysis.maxBuildableArea || 
              analysisData.analysis.maxBuildableArea <= 0 ||
              analysisData.analysis.maxBuildableArea === 'To be determined' ||
              analysisData.analysis.maxFloors === 'To be determined') {
            // No valid data - show free "no data" modal
            setShowNoDataModal(true);
            setAnalysisResult(null);
            setPendingAnalysis(null);
            setShowPayment(false);
            setError('');
          } else {
            // Valid data found - check payment status from backend AND frontend
            const isPaymentCompleted = backendPaymentCompleted || paymentCompleted;
            
            if (isPaymentCompleted) {
              setAnalysisResult(analysisData);
              setPendingAnalysis(null);
              setShowPayment(false);
              setPaymentCompleted(true); // Sync frontend state with backend
              setError('');
              setSuccessMessage('✅ Análisis completado exitosamente!');
              
              // SMP analysis completed - user can use "Analizar otro SMP" for new searches
              
              // Auto-scroll to investment analysis section
              scrollToInvestmentAnalysis();
            } else {
              // Valid data found - require payment to view
              setPendingAnalysis(analysisData);
              setShowPayment(true);
              setAnalysisResult(null); // Don't show results until payment
              setError('');
              
              // Auto-scroll to payment section
              scrollToPaymentSection();
            }
          }
        }
      } else {
        // Handle case where preview found no data (success: false)
        if (response.data && response.data.preview && !response.data.requiresPayment) {
          setShowNoDataModal(true);
          setAnalysisResult(null);
          setPendingAnalysis(null);
          setShowPayment(false);
          setError('');
        } else {
          setSuccessMessage('');
          setError(response.data?.message || 'No se pudo completar el análisis');
          setAnalysisResult(null);
          setPendingAnalysis(null);
          setShowPayment(false);
        }
      }
    } catch (error) {
      setSuccessMessage('');
      
      const errorMessage = error.code === 'ECONNABORTED' 
        ? 'El análisis tomó demasiado tiempo. Por favor, intenta nuevamente.'
        : error.response?.data?.message || 'Error al realizar el análisis de desarrollo. Verifica el código SMP e intenta nuevamente.';
      
      // Log error
      logError(errorMessage, { smp: smpCode, action: 'development_analysis' });
      
      setError(errorMessage);
      setAnalysisResult(null);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  // Payment callback functions
  const handlePaymentSuccess = (paymentData) => {
    setPaymentCompleted(true);
    setShowPayment(false);
    setAnalysisResult(pendingAnalysis); // Show the results after payment
    setPendingAnalysis(null);
    
    // SMP analysis completed - user can use "Analizar otro SMP" for new searches
  };

  // Listen for payment success from localStorage (cross-tab communication)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'mercadopago_payment_success' && e.newValue) {
        try {
          const paymentData = JSON.parse(e.newValue);
          
          if (paymentData.status === 'approved' && pendingAnalysis) {
            handlePaymentSuccess(paymentData);
            // Clear the localStorage item to prevent duplicate handling
            localStorage.removeItem('mercadopago_payment_success');
          }
        } catch (error) {
        }
      }
    };

    // Also check for existing payment success when component mounts
    const checkExistingPaymentSuccess = () => {
      const stored = localStorage.getItem('mercadopago_payment_success');
      if (stored && pendingAnalysis) {
        try {
          const paymentData = JSON.parse(stored);
          if (paymentData.status === 'approved') {
            handlePaymentSuccess(paymentData);
            localStorage.removeItem('mercadopago_payment_success');
          }
        } catch (error) {
        }
      }
    };

    // Check on mount
    checkExistingPaymentSuccess();

    // Listen for storage changes (cross-tab)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [pendingAnalysis]); // Dependency on pendingAnalysis so it re-runs when that changes

  const handlePaymentError = (error) => {
    setError(`Error en el pago: ${error}`);
  };

  const handleBackToSearch = () => {
    // Reset all states to go back to search
    setAnalysisResult(null);
    setPendingAnalysis(null);
    setShowPayment(false);
    setPaymentCompleted(false);
    setError('');
    setManualSMP('');
  };

  // Function to validate payment and then run analysis
  const validatePaymentAndAnalyze = async (paymentId, smp) => {
    try {
      setError('Validando pago...');
      
      const response = await axios.post(`${apiBaseUrl}/api/validate_payment`, {
        paymentId: paymentId,
        smp: smp
      });

      if (response.data.success) {
        setPaymentCompleted(true);
        setManualSMP(response.data.smp);
        setError('Pago validado! Ejecutando análisis...');
        
        // Wait a moment then trigger analysis
        setTimeout(() => {
          handleAnalyzeDevelopment();
        }, 1000);
      } else {
        setError(`Error de validación: ${response.data.message}`);
      }
    } catch (error) {
      setError(`Error validando pago: ${error.response?.data?.message || error.message}`);
    }
  };

  const formatNumber = (num) => {
    if (typeof num === 'number') {
      return num.toLocaleString('es-AR');
    }
    return num;
  };

  const calculateEstimatedSalePrice = (sellableArea, priceInfo) => {
    if (!sellableArea || !priceInfo || sellableArea === 'To be determined' || typeof sellableArea !== 'number') {
      return null;
    }
    
    const pricePerSqm = priceInfo.price;
    const totalValue = sellableArea * pricePerSqm;
    
    return {
      totalValue,
      pricePerSqm,
      sellableArea,
      zone: priceInfo.zone,
      basePrice: priceInfo.basePrice || priceInfo.price,
      condition: priceInfo.condition || 'standard',
      adjustmentFactor: priceInfo.totalAdjustmentFactor || priceInfo.adjustmentFactor || 1,
      estadoFactor: priceInfo.estadoFactor,
      antiguedadFactor: priceInfo.antiguedadFactor,
      antiguedad: priceInfo.antiguedad
    };
  };

  return (
    <div 
      id="development-analysis-section"
      style={{ 
        backgroundColor: '#f1f3f4', 
        minHeight: '100vh',
        padding: '10px'
      }}>
      <div className="container-fluid" style={{ maxWidth: '1400px' }}>
        
        {/* Header */}
        <div className="row mb-3 mb-md-4">
          <div className="col-12">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
              <div className="text-center text-md-start">
                <h1 className="mb-0 fw-bold fs-3 fs-md-1" style={{ color: '#1a202c' }}>
                  🏗️ Análisis de Desarrollo
                </h1>
                <p className="text-muted mb-0 fs-6 fs-md-5">Descubre el potencial de tu propiedad</p>
              </div>
              <button 
                onClick={onClose}
                className="btn btn-outline-secondary rounded-pill px-3 px-md-4 align-self-center"
                style={{ fontSize: '0.9rem' }}
              >
                ✕ Cerrar
              </button>
            </div>
          </div>
        </div>

        {/* Show "New Analysis" button when results are displayed */}
        {analysisResult && (
          <div className="row mb-4">
            <div className="col-12 text-center">
              <button
            onClick={() => {
              // Log new analysis action
              logUserAction(ACTIONS.NEW_ANALYSIS_STARTED, { previous_smp: manualSMP });
              
              // Clear all localStorage items related to payments and analysis
              localStorage.removeItem('showDevelopmentResults');
              localStorage.removeItem('paymentCompleted');
              localStorage.removeItem('mercadopago_payment_id');
              localStorage.removeItem('mercadopago_payment_success');
              localStorage.removeItem('pendingMercadoPagoPayment');
              localStorage.removeItem('lastAnalyzedSmp');
              localStorage.removeItem('mercadopago_url_params');
              
              
              // Redirect to clean homepage
              window.location.href = 'https://tasacionesya.com';
            }}
                className="btn btn-outline-primary btn-lg px-4 px-md-5"
                style={{
                  borderRadius: '50px',
                  borderWidth: '2px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                🏢 Analizar nuevo SMP
              </button>
              <p className="text-muted small mt-2 mb-0">
                Inicia un análisis completamente nuevo
              </p>
            </div>
          </div>
        )}

        {/* Input Section - Only show when no results */}
        {!analysisResult && (
          <div className="row mb-4 mb-md-5">
            <div className="col-lg-8 mx-auto">
              <div style={{
                background: 'white',
                borderRadius: '15px',
                padding: '20px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                border: '1px solid #e2e8f0'
              }} className="p-md-5">
                <div className="text-center mb-3 mb-md-4">
                  <div className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle mb-3" style={{ width: '60px', height: '60px' }}>
                    <FontAwesomeIcon icon={faBuilding} className="text-primary fs-3" />
                  </div>
                  <h3 className="fw-bold mb-2 fs-4 fs-md-3" style={{ color: '#2d3748' }}>Ingresa tu Código SMP</h3>
                  <p className="text-muted small">Obtén un análisis completo del potencial de desarrollo</p>
                  
                  {/* SMP Information */}
                  <div className="bg-info bg-opacity-10 rounded-3 p-2 p-md-3 mb-3 mb-md-4">
                    <div className="d-flex flex-column d-md-flex d-md-flex-row align-items-start">
                      <div className="bg-info bg-opacity-20 rounded-circle p-2 me-md-3 mb-2 mb-md-0 align-self-center" style={{ minWidth: '35px', height: '35px' }}>
                        <div className="text-info fw-bold small text-center">ℹ️</div>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="fw-bold text-info mb-2 text-center text-md-start">¿Dónde encontrar tu código SMP?</h6>
                        <div className="small text-muted">
                          <p className="mb-2">El código SMP (Sección-Manzana-Parcela) lo puedes encontrar en:</p>
                          <ul className="mb-2 ps-3">
                            <li><strong>Tu escritura de la propiedad</strong></li>
                            <li>En <a href="https://ciudad3d.buenosaires.gob.ar/" target="_blank" rel="noopener noreferrer" className="text-decoration-none fw-bold">ciudad3d.buenosaires.gob.ar</a> ingresando la dirección y haciendo click en el terreno</li>
                          </ul>
                          <p className="mb-0 small text-center text-md-start"><em>Formato ejemplo: 015-014C-014C</em></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12 col-md-8 mx-auto">
                      <div className="mb-3 mb-md-4">
                        <input
                          type="text"
                          placeholder="Ejemplo: 015-014C-014B"
                          value={manualSMP}
                          onChange={(e) => setManualSMP(e.target.value)}
                          className="form-control form-control-lg text-center"
                          style={{
                            borderRadius: '15px',
                            border: '2px solid #e2e8f0',
                            fontSize: '1rem',
                            fontFamily: 'monospace',
                            letterSpacing: '1px',
                            padding: '12px'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#4f46e5';
                            e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </div>
                      
                      <div className="text-center">
                        <button
                          onClick={handleAnalyzeDevelopment}
                          disabled={loadingAnalysis || !manualSMP}
                          className="btn btn-primary btn-lg rounded-pill px-4 px-md-5 py-2 py-md-3"
                          style={{ 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                            width: '100%',
                            maxWidth: '300px'
                          }}
                        >
                          {loadingAnalysis ? (
                            <>
                              <RotatingLines strokeColor="white" strokeWidth="3" width="25" />
                              <span className="ms-3">Analizando...</span>
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faCalculator} className="me-2" />
                              Analizar Potencial
                            </>
                          )}
                        </button>
                      </div>

                      {error && (
                        <div className="alert alert-danger mt-4 rounded-3">
                          <strong>Error:</strong> {error}
                        </div>
                      )}

                      {successMessage && (
                        <div className="alert alert-success mt-4 rounded-3">
                          <strong>{successMessage}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Section - Shows when analysis data is found but payment needed */}
        {showPayment && pendingAnalysis && (
          <div id="payment-section" className="row">
            <div className="col-md-8 mx-auto">
              <div style={{
                background: 'white',
                borderRadius: '20px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                padding: '40px',
                textAlign: 'center',
                marginBottom: '30px'
              }}>
                <div style={{ marginBottom: '30px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>🏗️</div>
                  <h2 style={{ color: '#28a745', fontWeight: 'bold', marginBottom: '15px' }}>
                    ¡Análisis Completado!
                  </h2>
                  <p style={{ color: '#6c757d', fontSize: '16px', lineHeight: '1.6', marginBottom: '20px' }}>
                    Encontramos información detallada para el SMP <strong>{manualSMP}</strong>.<br/>
                    Para ver el análisis completo de desarrollo inmobiliario, por favor realiza el pago:
                  </p>
                  
                  {/* Preview of what they'll get */}
                  <div style={{
                    backgroundColor: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '25px',
                    textAlign: 'left'
                  }}>
                    <h5 style={{ color: '#2c3e50', marginBottom: '15px', textAlign: 'center' }}>
                      📋 Incluye:
                    </h5>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                      <div>✅ Superficie máxima edificable</div>
                      <div>✅ Altura máxima permitida</div>
                      <div>✅ Cantidad de pisos posibles</div>
                      <div>✅ Análisis de zonificación</div>
                      <div>✅ Métricas de demanda</div>
                      <div>✅ Precios de referencia</div>
                      <div>✅ Oportunidades identificadas</div>
                      <div>✅ Información catastral completa</div>
                    </div>
                  </div>

                  {/* Email input for payment */}
                  <div style={{ marginBottom: '20px' }}>
                    <input
                      type="email"
                      placeholder="Tu email para el recibo (Opcional)"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '2px solid #e9ecef',
                        fontSize: '16px',
                        textAlign: 'center'
                      }}
                    />
                  </div>
                </div>

                {/* MercadoPago Payment Component */}
                <MercadoPagoPayment
                  title={`Análisis de Desarrollo - SMP ${manualSMP}`}
                  price={14900} // $14900 ARS for development analysis
                  description="Análisis completo de potencial de desarrollo inmobiliario"
                  userEmail={userEmail}
                  smp={manualSMP}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                />

                <div style={{ marginTop: '20px' }}>
                  <button
                    onClick={handleBackToSearch}
                    style={{
                      background: 'transparent',
                      color: '#6c757d',
                      border: '2px solid #dee2e6',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    ← Buscar otro SMP
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Section - Shows after payment */}
        {analysisResult && (
        <div className="row">
          <div className="col-12">
              
              {/* Key Metrics Dashboard - Most Important Info */}
              <div id="investment-analysis-section" className="mb-4 mb-md-5" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)'
              }}>
                <div className="p-3 p-md-5 text-white">
                  <div className="text-center mb-4 mb-md-5">
                    <h1 className="fs-2 fs-md-1 fw-bold mb-3">
                      💎 Análisis de Inversión
                    </h1>
                    <p className="fs-5 fs-md-4 opacity-90">{analysisResult.address}</p>
                    <p className="small fs-md-6 opacity-75">{analysisResult.analysis.barrio} • {analysisResult.analysis.zoning}</p>
                  </div>
                  
                  {/* Enhanced Construction & Financial Analysis */}
                  <div className="row g-3 g-md-4 mb-3 mb-md-4">
                    <div className="col-12 col-lg-6 mb-3 mb-lg-0">
                      <div className="position-relative h-100" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
                        borderRadius: '20px',
                        padding: '25px 20px',
                        boxShadow: '0 20px 60px rgba(102, 126, 234, 0.4)',
                        color: 'white',
                        overflow: 'hidden',
                        minHeight: '300px'
                      }}>
                        {/* Animated background pattern */}
                        <div className="position-absolute w-100 h-100 top-0 start-0" style={{
                          background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                          opacity: 0.5
                        }}></div>
                        
                        <div className="position-relative">
                          <div className="text-center mb-3 mb-md-4">
                            <div className="d-inline-flex align-items-center justify-content-center bg-white bg-opacity-20 rounded-circle mb-3" style={{ width: '60px', height: '60px' }}>
                              <span className="fs-2">🏗️</span>
                            </div>
                            <h4 className="fw-bold mb-2 fs-5">Potencial Constructivo</h4>
                            <p className="opacity-90 mb-0 small">Todo lo que puedes construir legalmente</p>
                </div>
                          
                          <div className="row g-2 g-md-3 mb-3 mb-md-4">
                      <div className="col-6">
                              <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px',
                                padding: '15px 10px',
                                textAlign: 'center',
                                border: '1px solid rgba(255,255,255,0.1)'
                              }}>
                                <div className="fs-3 fs-md-2 fw-bold mb-1">{analysisResult.analysis.maxFloors}</div>
                                <div className="fw-bold small mb-1">PISOS</div>
                                <div className="small opacity-75">Altura permitida</div>
                              </div>
                      </div>
                      <div className="col-6">
                              <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px',
                                padding: '15px 10px',
                                textAlign: 'center',
                                border: '1px solid rgba(255,255,255,0.1)'
                              }}>
                                <div className="fs-3 fs-md-2 fw-bold mb-1" style={{ fontSize: '1.2rem' }}>{formatNumber(analysisResult.analysis.maxBuildableArea)}</div>
                                <div className="fw-bold small mb-1">M² TOTALES</div>
                                <div className="small opacity-75">Superficie máxima</div>
                              </div>
                      </div>
                    </div>
                    
                          <div style={{
                            background: 'linear-gradient(135deg, rgba(255,215,0,0.2) 0%, rgba(255,255,255,0.1) 100%)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '12px',
                            padding: '20px 15px',
                            textAlign: 'center',
                            border: '1px solid rgba(255,215,0,0.3)'
                          }}>
                            <div className="fs-4 fs-md-3 fw-bold mb-2 text-warning">{formatNumber(analysisResult.analysis.sellableArea)}</div>
                            <div className="fw-bold mb-1">M² VENDIBLES</div>
                            <div className="small opacity-75">Superficie que genera ingresos</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-12 col-lg-6">
                      <div className="position-relative h-100" style={{
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 50%, #11998e 100%)',
                        borderRadius: '20px',
                        padding: '25px 20px',
                        boxShadow: '0 20px 60px rgba(17, 153, 142, 0.4)',
                        color: 'white',
                        overflow: 'hidden',
                        minHeight: '300px'
                      }}>
                        {/* Animated background pattern */}
                        <div className="position-absolute w-100 h-100 top-0 start-0" style={{
                          background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M30 30l15-15v30z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                          opacity: 0.4
                        }}></div>
                        
                        <div className="position-relative">
                          <div className="text-center mb-3 mb-md-4">
                            <div className="d-inline-flex align-items-center justify-content-center bg-white bg-opacity-20 rounded-circle mb-3" style={{ width: '60px', height: '60px' }}>
                              <span className="fs-2">💰</span>
                            </div>
                            <h4 className="fw-bold mb-2 fs-5">Análisis Financiero</h4>
                            <p className="opacity-90 mb-0 small">Tu retorno de inversión estimado</p>
                          </div>
                          
                          <div className="row g-3 mb-4">
                        <div className="col-6">
                              <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '15px',
                                padding: '15px 8px',
                                textAlign: 'center',
                                border: '1px solid rgba(255,255,255,0.1)'
                              }}>
                                <div className="fw-bold mb-1" style={{ fontSize: '0.9rem' }}>US${formatNumber(analysisResult.analysis.maxBuildableArea * 950)}</div>
                                <div className="fw-bold small mb-1">INVERSIÓN</div>
                                <div className="small opacity-75">Aproximado (estándar)</div>
                                <div className="small opacity-60" style={{ fontSize: '0.7rem' }}>
                                  Varía según calidad: básica, estándar, premium
                                </div>
                          </div>
                        </div>
                        <div className="col-6">
                              <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '15px',
                                padding: '15px 8px',
                                textAlign: 'center',
                                border: '1px solid rgba(255,255,255,0.1)'
                              }}>
                                <div className="fw-bold mb-1" style={{ fontSize: '0.9rem' }}>
                                  {(() => {
                                    const estimatedPrice = calculateEstimatedSalePrice(
                                      analysisResult.analysis.sellableArea, 
                                      analysisResult.analysis.priceInfo
                                    );
                                    if (estimatedPrice) {
                                      return `US$${formatNumber(estimatedPrice.totalValue)}`;
                                    }
                                    return 'TBD';
                                  })()}
                                </div>
                                <div className="fw-bold small mb-1">INGRESOS</div>
                                <div className="small opacity-75">Valor de venta</div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="row g-3">
                            <div className="col-6">
                              <div style={{
                                background: 'linear-gradient(135deg, rgba(255,215,0,0.3) 0%, rgba(255,255,255,0.15) 100%)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '15px',
                                padding: '15px 8px',
                                textAlign: 'center',
                                border: '2px solid rgba(255,215,0,0.4)'
                              }}>
                                <div className="fw-bold mb-1 text-success" style={{ fontSize: '0.9rem' }}>
                                  {(() => {
                                    const estimatedPrice = calculateEstimatedSalePrice(
                                      analysisResult.analysis.sellableArea, 
                                      analysisResult.analysis.priceInfo
                                    );
                                    if (estimatedPrice) {
                                      const constructionCost = analysisResult.analysis.maxBuildableArea * 950;
                                      const profit = estimatedPrice.totalValue - constructionCost;
                                      return `US$${formatNumber(profit)}`;
                                    }
                                    return 'TBD';
                                  })()}
                                </div>
                                <div className="fw-bold small mb-1 text-dark">GANANCIA</div>
                                <div className="small text-dark opacity-75">Utilidad aproximada</div>
                              </div>
                            </div>
                            <div className="col-6">
                              <div style={{
                                background: 'linear-gradient(135deg, rgba(255,215,0,0.3) 0%, rgba(255,255,255,0.15) 100%)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '15px',
                                padding: '15px 8px',
                                textAlign: 'center',
                                border: '2px solid rgba(255,215,0,0.4)'
                              }}>
                                <div className="fw-bold mb-1 text-primary" style={{ fontSize: '1.1rem' }}>
                                  {(() => {
                                    const estimatedPrice = calculateEstimatedSalePrice(
                                      analysisResult.analysis.sellableArea, 
                                      analysisResult.analysis.priceInfo
                                    );
                                    if (estimatedPrice) {
                                      const constructionCost = analysisResult.analysis.maxBuildableArea * 950;
                                      const profit = estimatedPrice.totalValue - constructionCost;
                                      const roi = Math.round((profit / constructionCost) * 100);
                                      return `${roi}%`;
                                    }
                                    return '149%';
                                  })()}
                                </div>
                                <div className="fw-bold small mb-1 text-dark">ROI ESTIMADO</div>
                                <div className="small text-dark opacity-75">Retorno sobre inversión</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Row - Market Intelligence - Enhanced Design */}
                  <div className="row g-4">
                    <div className="col-lg-4">
                      <div className="position-relative h-100" style={{
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                        borderRadius: '20px',
                        padding: '30px 25px',
                        boxShadow: '0 12px 35px rgba(255, 107, 107, 0.3)',
                        color: '#1a1a1a',
                        transform: 'translateY(0)',
                        transition: 'all 0.3s ease'
                      }}>
                        <div className="mb-3">
                          <h6 className="fw-bold mb-2 text-white">¿Qué Tan Buscado Es?</h6>
                          <div className="display-6 fw-bold mb-2 text-white">
                            {analysisResult.analysis.demandInfo?.Demanda || 'Estándar'}
                          </div>
                          <div className="bg-white bg-opacity-90 rounded-pill px-3 py-1 d-inline-block">
                            <small className="fw-bold text-dark">Demanda en {analysisResult.analysis.barrio}</small>
                          </div>
                        </div>
                        
                        <div className="small text-white">
                          <div className="d-flex align-items-center">
                            <div className="bg-white rounded-circle p-1 me-2" style={{ width: '6px', height: '6px' }}></div>
                            Nivel de interés de compradores
                          </div>
                      </div>
                      </div>
                    </div>
                    
                    <div className="col-lg-4">
                      <div className="position-relative h-100" style={{
                        background: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)',
                        borderRadius: '20px',
                        padding: '30px 25px',
                        boxShadow: '0 12px 35px rgba(78, 205, 196, 0.3)',
                        color: '#1a1a1a',
                        transform: 'translateY(0)',
                        transition: 'all 0.3s ease'
                      }}>
                        <div className="mb-3">
                          <h6 className="fw-bold mb-2 text-white">¿Qué Construir?</h6>
                          <div className="display-6 fw-bold mb-2 text-white">
                            {analysisResult.analysis.demandInfo?.['Tipos más vendibles (ambientes)'] || '2-3 amb'}
                          </div>
                          <div className="bg-white bg-opacity-90 rounded-pill px-3 py-1 d-inline-block">
                            <small className="fw-bold text-dark">Producto Ideal</small>
                          </div>
                        </div>
                        
                        <div className="small text-white">
                          <div className="d-flex align-items-center">
                            <div className="bg-white rounded-circle p-1 me-2" style={{ width: '6px', height: '6px' }}></div>
                            Lo que más se vende aquí
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-lg-4">
                      <div className="position-relative h-100" style={{
                        background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                        borderRadius: '20px',
                        padding: '30px 25px',
                        boxShadow: '0 12px 35px rgba(168, 237, 234, 0.4)',
                        color: '#2d3748',
                        transform: 'translateY(0)',
                        transition: 'all 0.3s ease'
                      }}>
                        <div className="position-absolute top-0 end-0 m-3">
                          <div className="bg-white bg-opacity-30 rounded-circle p-2">
                            <div className="fs-4">💵</div>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <h6 className="fw-bold mb-2 text-dark">¿A Cuánto Vender?</h6>
                          <div className="display-6 fw-bold mb-2 text-primary">
                            {(() => {
                              const estimatedPrice = calculateEstimatedSalePrice(
                                analysisResult.analysis.sellableArea, 
                                analysisResult.analysis.priceInfo
                              );
                              if (estimatedPrice) {
                                return `US$${formatNumber(estimatedPrice.pricePerSqm)}`;
                              }
                              return 'A calcular';
                            })()}
                          </div>
                          <div className="bg-white bg-opacity-90 rounded-pill px-3 py-1 d-inline-block">
                            <small className="fw-bold text-dark">Precio por m²</small>
                          </div>
                        </div>
                        
                        <div className="small text-dark">
                          <div className="d-flex align-items-center">
                            <div className="bg-primary rounded-circle p-1 me-2" style={{ width: '6px', height: '6px' }}></div>
                            Valor de mercado actual
                      </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Key Recommendation */}
                  <div className="mt-4 text-center">
                    <div className="bg-white bg-opacity-10 rounded-4 p-4 d-inline-block">
                      <h5 className="fw-bold mb-2">
                        🏆 Recomendación: Desarrolla {analysisResult.analysis.demandInfo?.['Tipos más vendibles (ambientes)'] || '2-3 ambientes'}
                      </h5>
                      <p className="mb-0 opacity-75">
                        {analysisResult.analysis.demandInfo?.['Motivo/Notas'] || 'Producto con alta demanda en la zona'}
                      </p>
                        </div>
                        </div>
                        </div>
                      </div>

              {/* Technical Details Section */}
              <div className="row mb-5">
                <div className="col-12">
                  <div style={{
                    background: 'white',
                    borderRadius: '20px',
                    padding: '40px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div className="text-center mb-5">
                      <h2 className="fw-bold mb-2" style={{ color: '#2d3748' }}>🔧 Información Técnica</h2>
                      <p className="text-muted fs-5">Detalles técnicos y normativos del análisis</p>
                        </div>
                    
                    {/* Technical Grid */}
                    <div className="row g-4">
                      {/* Property Information */}
                      <div className="col-lg-6">
                        <div className="bg-light rounded-4 p-4 h-100">
                          <div className="d-flex align-items-center mb-3">
                            <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                              <FontAwesomeIcon icon={faBuilding} className="text-primary" />
                        </div>
                            <h5 className="fw-bold mb-0" style={{ color: '#2d3748' }}>Datos de la Propiedad</h5>
                      </div>

                          <div className="row g-3">
                        <div className="col-6">
                              <div className="border rounded-3 p-3 text-center">
                                <small className="text-muted d-block">SMP</small>
                                <strong style={{ fontFamily: 'monospace' }}>{analysisResult.analysis.smp}</strong>
                              </div>
                        </div>
                        <div className="col-6">
                              <div className="border rounded-3 p-3 text-center">
                                <small className="text-muted d-block">Zonificación</small>
                                <strong>{analysisResult.analysis.zoning}</strong>
                        </div>
                      </div>
                        <div className="col-6">
                              <div className="border rounded-3 p-3 text-center">
                                <small className="text-muted d-block">Barrio</small>
                                <strong>{analysisResult.analysis.barrio}</strong>
                              </div>
                        </div>
                        <div className="col-6">
                              <div className="border rounded-3 p-3 text-center">
                                <small className="text-muted d-block">Distrito</small>
                                <strong>{analysisResult.analysis.distrito}</strong>
                        </div>
                      </div>
                        </div>
                        </div>
                      </div>

                      {/* Building Regulations */}
                      <div className="col-lg-6">
                        <div className="bg-light rounded-4 p-4 h-100">
                          <div className="d-flex align-items-center mb-3">
                            <div className="bg-success bg-opacity-10 rounded-circle p-2 me-3">
                              <FontAwesomeIcon icon={faCalculator} className="text-success" />
                            </div>
                            <h5 className="fw-bold mb-0" style={{ color: '#2d3748' }}>Parámetros Normativos</h5>
                          </div>
                          
                          <div className="row g-3">
                            <div className="col-6">
                              <div className="border rounded-3 p-3 text-center">
                                <small className="text-muted d-block">FOT</small>
                                <strong>{analysisResult.analysis.fot}</strong>
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="border rounded-3 p-3 text-center">
                                <small className="text-muted d-block">Altura Máxima</small>
                                <strong>{analysisResult.analysis.alturaMaxima}m</strong>
                        </div>
                    </div>
                        <div className="col-6">
                              <div className="border rounded-3 p-3 text-center">
                                <small className="text-muted d-block">Área Parcela</small>
                                <strong>{formatNumber(analysisResult.analysis.areaParcela)} m²</strong>
                              </div>
                        </div>
                        <div className="col-6">
                              <div className="border rounded-3 p-3 text-center">
                                <small className="text-muted d-block">Retiros</small>
                                <strong>{analysisResult.analysis.retiros || 'Estándar'}</strong>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>

                      {/* Development Metrics */}
                      <div className="col-lg-6">
                        <div className="bg-light rounded-4 p-4 h-100">
                          <div className="d-flex align-items-center mb-3">
                            <div className="bg-warning bg-opacity-10 rounded-circle p-2 me-3">
                              <FontAwesomeIcon icon={faChartLine} className="text-warning" />
                            </div>
                            <h5 className="fw-bold mb-0" style={{ color: '#2d3748' }}>Métricas de Desarrollo</h5>
                    </div>
                    
                          <div className="row g-3">
                            <div className="col-12">
                              <div className="border rounded-3 p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-muted">Superficie Edificable:</span>
                                  <strong>{formatNumber(analysisResult.analysis.maxBuildableArea)} m²</strong>
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="border rounded-3 p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-muted">Superficie Vendible:</span>
                                  <strong className="text-success">{formatNumber(analysisResult.analysis.sellableArea)} m²</strong>
                          </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="border rounded-3 p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-muted">Aprovechamiento:</span>
                                  <strong className="text-primary">{Math.round((analysisResult.analysis.sellableArea / analysisResult.analysis.areaParcela) * 100)}%</strong>
                          </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Market Data */}
                      <div className="col-lg-6">
                        <div className="bg-light rounded-4 p-4 h-100">
                          <div className="d-flex align-items-center mb-3">
                            <div className="bg-info bg-opacity-10 rounded-circle p-2 me-3">
                              <FontAwesomeIcon icon={faUsers} className="text-info" />
                            </div>
                            <h5 className="fw-bold mb-0" style={{ color: '#2d3748' }}>Análisis de Mercado</h5>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="border rounded-3 p-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-muted">Demanda Local:</span>
                                <strong className="text-danger">{analysisResult.analysis.demandInfo?.Demanda || 'Estándar'}</strong>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-muted">Producto Ideal:</span>
                                <strong className="text-success">{analysisResult.analysis.demandInfo?.['Tipos más vendibles (ambientes)'] || '2-3 amb'}</strong>
                        </div>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted">Precio de Venta:</span>
                                <strong className="text-primary">
                                  {(() => {
                                    const estimatedPrice = calculateEstimatedSalePrice(
                                      analysisResult.analysis.sellableArea, 
                                      analysisResult.analysis.priceInfo
                                    );
                                    if (estimatedPrice) {
                                      return `US$${formatNumber(estimatedPrice.pricePerSqm)}/m²`;
                                    }
                                    return 'A calcular';
                                  })()}
                                </strong>
                          </div>
                        </div>
                            
                            {analysisResult.analysis.demandInfo?.['Motivo/Notas'] && (
                              <div className="border rounded-3 p-3 bg-white">
                                <small className="text-muted d-block mb-1">Observaciones del Mercado:</small>
                                <span className="small">{analysisResult.analysis.demandInfo['Motivo/Notas']}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Additional Technical Info */}
                    {(analysisResult.analysis.manzanaTipo || analysisResult.analysis.caracteristicasEspeciales) && (
                      <div className="mt-4">
                        <div className="bg-info bg-opacity-10 rounded-4 p-4">
                          <h6 className="fw-bold mb-3 text-info">ℹ️ Información Adicional</h6>
                          <div className="row g-3">
                            {analysisResult.analysis.manzanaTipo && (
                              <div className="col-md-6">
                                <div className="d-flex justify-content-between">
                                  <span className="text-muted">Tipo de Manzana:</span>
                                  <strong>{analysisResult.analysis.manzanaTipo}</strong>
                  </div>
                                {analysisResult.analysis.manzanaTipo === 'Atípica' && analysisResult.analysis.manzanaDisposicion && (
                                  <div className="d-flex justify-content-between mt-1">
                                    <span className="text-muted small">Disposición:</span>
                                    <span className="small">{analysisResult.analysis.manzanaDisposicion}</span>
                      </div>
                    )}
                      </div>
                    )}
                            
                            {analysisResult.analysis.caracteristicasEspeciales && (
                              <div className="col-md-6">
                                <div className="d-flex justify-content-between">
                                  <span className="text-muted">Características Especiales:</span>
                                  <strong>{analysisResult.analysis.caracteristicasEspeciales}</strong>
                                </div>
                      </div>
                    )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>


            </div>
          </div>
        )}

        {/* No Results State - Only show when not displaying payment */}
        {!analysisResult && !showPayment && (
          <div className="row">
            <div className="col-md-8 mx-auto">
              <div className="text-center py-5" style={{
                background: 'white',
                borderRadius: '20px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
              }}>
                  <FontAwesomeIcon 
                    icon={faBuilding} 
                  style={{ fontSize: '4rem', color: '#cbd5e0' }} 
                  className="mb-4" 
                />
                <h4 className="text-muted mb-3">Análisis de Desarrollo</h4>
                <p className="text-muted">
                  Ingresa un código SMP para obtener un análisis completo del potencial de tu propiedad
                  </p>
                </div>
                </div>
              </div>
            )}

        {/* No Data Found Modal */}
        {showNoDataModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none' }}>
              <div className="modal-body p-5 text-center">
                <div className="mb-4">
                  <div className="d-inline-flex align-items-center justify-content-center bg-warning bg-opacity-10 rounded-circle mb-3" style={{ width: '80px', height: '80px' }}>
                    <span className="fs-1">⚠️</span>
                  </div>
                </div>
                
                <h4 className="fw-bold mb-3" style={{ color: '#2d3748' }}>
                  No se encontraron datos
                </h4>
                
                <p className="text-muted mb-4">
                  No pudimos encontrar información de desarrollo para el código SMP <strong>{manualSMP}</strong>.
                </p>
                
                <div className="bg-light rounded-3 p-3 mb-4">
                  <small className="text-muted">
                    <strong>Posibles causas:</strong><br/>
                    • El código SMP no existe<br/>
                    • El terreno no tiene datos de edificabilidad disponibles<br/>
                    • Los datos de pisos o superficie están incompletos<br/>
                    • Error en el formato del código SMP
                  </small>
                </div>
                
                <div className="d-flex gap-3 justify-content-center">
                  <button 
                    className="btn btn-outline-secondary rounded-pill px-4"
                    onClick={() => setShowNoDataModal(false)}
                  >
                    Intentar otro SMP
                  </button>
                  <button 
                    className="btn btn-primary rounded-pill px-4"
                    onClick={() => {
                      setShowNoDataModal(false);
                      setManualSMP('');
                    }}
                    style={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none'
                    }}
                  >
                    Limpiar formulario
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default DevelopmentAnalysis;